<!-- CodingStatusSelect.vue -->
<template>
  <Select
    :model-value="modelValue"
    @update:model-value="handleUpdate"
    :disabled="disabled"
  >
    <SelectTrigger class="w-full">
      <SelectValue>
        <template v-if="modelValue">
          <div :class="getStatusClass(modelValue)">
            {{ findLabelByValue(modelValue) }}
            <Loader2
              v-if="isLoading"
              class="animate-spin h-4 w-4 inline ml-1"
            />
          </div>
        </template>
        <span v-else class="text-gray-400">Select status...</span>
      </SelectValue>
    </SelectTrigger>
    <SelectContent>
      <SelectGroup>
        <SelectItem
          v-for="choice in displayChoices"
          :key="choice.value"
          :value="choice.value"
        >
          <div :class="getStatusClass(choice.value)">
            {{ choice.label }}
          </div>
        </SelectItem>
      </SelectGroup>
    </SelectContent>
  </Select>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { useCodingStatusStore } from '@/stores/codingStatus'
import { PROGRESS_STATUS, progressStatusLabels } from '@/const/progressStatus'

const ALLOWED_STATUSES = [
  PROGRESS_STATUS.NOT_STARTED,
  PROGRESS_STATUS.IN_PROGRESS,
  PROGRESS_STATUS.PASSED,
  PROGRESS_STATUS.FAILED,
]

const props = defineProps<{
  modelValue: string
  disabled?: boolean
  isLoading?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const codingStatusStore = useCodingStatusStore()

const displayChoices = computed(() => {
  const fromStore = codingStatusStore.codingStatusChoices.filter(c => ALLOWED_STATUSES.includes(c.value as any))
  if (fromStore.length > 0) return fromStore
  return ALLOWED_STATUSES.map(value => ({ value, label: progressStatusLabels[value] }))
})

onMounted(async () => {
  if (codingStatusStore.codingStatusChoices.length === 0) {
    await codingStatusStore.fetchCodingStatusChoices()
  }
})

const handleUpdate = (value: unknown) => {
  if (typeof value === 'string') {
    emit('update:modelValue', value)
  } else if (value === null) {
    emit('update:modelValue', '')
  }
}

const findLabelByValue = (value: string) => {
  const choice = displayChoices.value.find(c => c.value === value)
  return choice?.label || progressStatusLabels[value] || value
}

const getStatusClass = (status: string): string => {
  const baseClasses = 'px-2 py-1 rounded-full text-xs flex items-center gap-1'
  const label = findLabelByValue(status).toLowerCase()

  if (label.includes('in progress') || label.includes('scheduled')) {
    return `${baseClasses} bg-blue-100 text-blue-800`
  }
  if (label.includes('passed')) {
    return `${baseClasses} bg-lime-100 text-lime-800`
  }
  if (label.includes('failed') || label.includes("didn't pass")) {
    return `${baseClasses} bg-red-100 text-red-800`
  }
  return `${baseClasses} bg-gray-100 text-gray-800`
}
</script> 